import { useEffect, useLayoutEffect, useRef, useState } from 'react';

interface AxisLabelPoint {
  axisLabel: string;
}

interface ChartAxisLabelsProps {
  points: AxisLabelPoint[];
  visibleIndices: Set<number>;
  active: number | null;
  isDark?: boolean;
  leftPct: (index: number) => number;
}

/**
 * Edge padding so the first and last month labels never get clipped by the
 * plot boundary. Anything inside these bands is pinned to the edge instead of
 * being centered on its data point.
 */
const START_THRESHOLD = 4;
const END_THRESHOLD = 96;
const EDGE_INSET = 4;
/** Minimum horizontal gap (px) allowed between two neighbouring labels. */
const MIN_GAP = 8;

function sameSet(a: Set<number>, b: Set<number>): boolean {
  if (a.size !== b.size) return false;
  for (const value of a) {
    if (!b.has(value)) return false;
  }
  return true;
}

/**
 * Absolute-positioned x-axis labels aligned to each data point. Because the
 * labels sit on real x coordinates (instead of equal-width flex cells), the
 * leftmost/rightmost ones line up exactly with the curve and stay inside the
 * chart with a small inset.
 *
 * Labels are only thinned by count upstream, which is not enough when a month
 * boundary sits right next to the first data point (e.g. the series starts
 * mid-November, so "Nov" and "Dec" end up a single week apart). So on top of
 * that we measure each label's real width and drop any that would overlap its
 * neighbour — the trailing label always survives so the axis keeps both ends.
 */
export default function ChartAxisLabels({
  points,
  visibleIndices,
  active,
  isDark = false,
  leftPct,
}: ChartAxisLabelsProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const labelRefs = useRef<Map<number, HTMLSpanElement>>(new Map());
  const leftPctRef = useRef(leftPct);
  leftPctRef.current = leftPct;

  const [hidden, setHidden] = useState<Set<number>>(() => new Set());
  const [width, setWidth] = useState(0);

  const candidateKey = points
    .map((_, i) => (visibleIndices.has(i) ? i : -1))
    .filter((i) => i >= 0)
    .join(',');

  // Track the container width so overlap is recomputed whenever it resizes.
  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver((entries) => {
      const next = entries[0]?.contentRect.width ?? 0;
      setWidth((prev) => (Math.abs(prev - next) > 0.5 ? next : prev));
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const chartWidth = container.clientWidth;
    if (chartWidth <= 0) return;

    const indices = candidateKey ? candidateKey.split(',').map(Number) : [];
    const rects = indices.flatMap((i) => {
      const el = labelRefs.current.get(i);
      if (!el) return [];
      const labelWidth = el.offsetWidth;
      const left = leftPctRef.current(i);
      const center = (left / 100) * chartWidth;
      let start: number;
      if (left <= START_THRESHOLD) start = EDGE_INSET;
      else if (left >= END_THRESHOLD) start = chartWidth - EDGE_INSET - labelWidth;
      else start = center - labelWidth / 2;
      return [{ i, start, end: start + labelWidth }];
    });

    const nextHidden = new Set<number>();
    let lastEnd = -Infinity;
    rects.forEach((rect, order) => {
      const isLast = order === rects.length - 1;
      if (rect.start < lastEnd + MIN_GAP) {
        if (isLast) {
          // The final label must stay visible — drop the nearest kept one.
          for (let k = order - 1; k >= 0; k -= 1) {
            if (!nextHidden.has(rects[k].i)) {
              nextHidden.add(rects[k].i);
              break;
            }
          }
        } else {
          nextHidden.add(rect.i);
          return;
        }
      }
      lastEnd = Math.max(lastEnd, rect.end);
    });

    setHidden((prev) => (sameSet(prev, nextHidden) ? prev : nextHidden));
  }, [candidateKey, width]);

  return (
    <div ref={containerRef} className="relative mt-3 h-4 select-none">
      {points.map((point, i) => {
        if (!visibleIndices.has(i)) return null;

        const left = leftPct(i);
        const nearStart = left <= START_THRESHOLD;
        const nearEnd = left >= END_THRESHOLD;
        const positionClass = nearStart
          ? 'left-1 text-left'
          : nearEnd
            ? 'right-1 text-right'
            : '-translate-x-1/2 text-center';
        const colorClass =
          active === i
            ? isDark
              ? 'text-background-50'
              : 'text-foreground-900'
            : isDark
              ? 'text-background-200/70'
              : 'text-foreground-500';
        const isHidden = hidden.has(i);

        return (
          <span
            key={`${point.axisLabel}-${i}`}
            ref={(el) => {
              if (el) labelRefs.current.set(i, el);
              else labelRefs.current.delete(i);
            }}
            aria-hidden={isHidden || undefined}
            className={`absolute top-0 whitespace-nowrap text-[10px] font-medium md:text-xs ${positionClass} ${colorClass} ${isHidden ? 'invisible' : ''}`}
            style={nearStart || nearEnd ? undefined : { left: `${left}%` }}
          >
            {point.axisLabel}
          </span>
        );
      })}
    </div>
  );
}